import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, Calendar, Users, Megaphone, MapPin, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage({ user }) {
  const LOGO_URL = "https://customer-assets.emergentagent.com/job_f697ed33-4c13-453f-bfe2-564f171bf1d2/artifacts/4slporqq_1%20%281%29.png";
  const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_239d7274-ca2b-497c-8252-9d7915b2e0dd/artifacts/cgzm8zjb_Group%20of%20Senior%20Woman%20Enjoying%20Being%20Together%20at%20a%20Cafe.jpg";

  const displayName = user?.display_name || user?.full_name?.split(' ')[0] || 'Member';
  const [newPostsCount, setNewPostsCount] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [welcomeMessage, setWelcomeMessage] = useState(null);
  const [welcomeChapter, setWelcomeChapter] = useState('');

  useEffect(() => {
    fetchNotifications();
    fetchWelcomeMessage();
  }, []);

  const fetchWelcomeMessage = async () => {
    try {
      const res = await axios.get(`${API}/chapters/welcome-message`);
      if (res.data.welcome_message) {
        setWelcomeMessage(res.data.welcome_message);
        setWelcomeChapter(res.data.chapter || '');
      }
    } catch { /* ignore */ }
  };

  const dismissWelcome = async () => {
    setWelcomeMessage(null);
    try {
      await axios.post(`${API}/chapters/dismiss-welcome`);
    } catch { /* ignore */ }
  };

  const fetchNotifications = async () => {
    try {
      // Get last viewed timestamp from localStorage
      const lastViewedPosts = localStorage.getItem('lastViewedPosts');
      const lastViewedMessages = localStorage.getItem('lastViewedMessages');
      
      // Fetch posts count since last view
      const postsResponse = await axios.get(`${API}/posts/unread-count${lastViewedPosts ? `?since=${lastViewedPosts}` : ''}`);
      setNewPostsCount(postsResponse.data.count || 0);
      
      // Fetch unread messages count
      const messagesResponse = await axios.get(`${API}/messages/unread-count`);
      setUnreadMessagesCount(messagesResponse.data.count || 0);
    } catch (error) {
      console.error('Failed to load homepage data:', error);
    }
  };

  const navCards = [
    { 
      path: '/community', 
      label: 'Community Feed', 
      icon: Home, 
      color: '#5b7f95',
      description: 'Share updates and connect with members',
      badge: newPostsCount
    },
    { 
      path: '/events', 
      label: 'Events', 
      icon: Calendar, 
      color: '#cb9944',
      description: 'Browse and book upcoming events',
      badge: 0
    },
    { 
      path: '/directory', 
      label: 'Member Directory', 
      icon: Users, 
      color: '#5b7f95',
      description: 'Find and connect with members',
      badge: 0
    },
    { 
      path: '/marketplace', 
      label: 'Marketplace', 
      icon: Megaphone, 
      color: '#cb9944',
      description: 'Advertise and browse member services',
      badge: 0
    },
  ];

  return (
    <div className="min-h-screen bg-[#f8f9fa]" data-testid="home-page">
      {/* Hero Section with Image */}
      <div className="relative h-[300px] md:h-[400px] overflow-hidden">
        <img 
          src={HERO_IMAGE} 
          alt="Women networking" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/60" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-6">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            Welcome back, {displayName}!
          </h1>
          <p className="text-lg md:text-xl opacity-90">Women Supporting Women</p>
        </div>
      </div>

      {/* Chapter Welcome Message */}
      {welcomeMessage && (
        <div className="max-w-4xl mx-auto px-6 -mt-6 relative z-20 mb-4" data-testid="welcome-banner">
          <div className="bg-white border-l-4 border-[#cb9944] rounded-xl shadow-lg p-5 relative">
            <button 
              onClick={dismissWelcome} 
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              data-testid="dismiss-welcome-btn"
            >
              <X className="w-5 h-5" />
            </button>
            <p className="text-xs text-[#cb9944] font-semibold uppercase tracking-wide mb-1">
              Welcome to {welcomeChapter}
            </p>
            <p className="text-[#2d3748] text-sm leading-relaxed pr-8 whitespace-pre-line">
              {welcomeMessage}
            </p>
          </div>
        </div>
      )}

      {/* Navigation Cards */}
      <div className="max-w-4xl mx-auto px-6 -mt-12 relative z-10 pb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {navCards.map(({ path, label, icon: Icon, color, description, badge }) => (
            <Link key={path} to={path}>
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full flex flex-col items-center text-center group">
                <div 
                  className="relative w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${color}15` }}
                >
                  <Icon className="w-8 h-8" style={{ color }} />
                  {badge > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </div>
                <h3 className="font-semibold text-[#2d3748] mb-2">{label}</h3>
                <p className="text-xs text-[#64748b] hidden md:block">{description}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Join MBN Button - Prominent CTA */}
      <div className="max-w-4xl mx-auto px-6 pb-6">
        <a href="https://form.jotform.com/260273419014551" target="_blank" rel="noopener noreferrer">
          <div className="bg-gradient-to-r from-[#cb9944] to-[#b08435] rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Join Mom's Biz Network!</h2>
            <p className="text-white/90 mb-4">Become part of our growing community of women entrepreneurs</p>
            <Button className="bg-white text-[#cb9944] hover:bg-gray-100 rounded-full px-8 py-3 text-lg font-semibold">
              Apply for Membership
            </Button>
          </div>
        </a>
      </div>

      {/* Chapters Section - Prominent CTA */}
      <div className="max-w-4xl mx-auto px-6 pb-12">
        <Link to="/chapters">
          <div className="bg-gradient-to-r from-[#5b7f95] to-[#4a6b80] rounded-2xl p-8 text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                  <MapPin className="w-7 h-7" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-1">Explore Our Chapters</h2>
                  <p className="text-white/80">Find your local chapter and meet your Chapter Leader</p>
                </div>
              </div>
              <Button variant="secondary" className="bg-white text-[#5b7f95] hover:bg-gray-100 rounded-full hidden md:flex">
                View Chapters
              </Button>
            </div>
          </div>
        </Link>
      </div>

      {/* Website Link Section */}
      <div className="max-w-4xl mx-auto px-6 pb-6">
        <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 text-center">
          <p className="text-[#64748b] mb-3">
            Quick access to our website – browse and buy from fellow members!
          </p>
          <a 
            href="https://www.momsbiznetwork.co.za" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#5b7f95] hover:text-[#4a6b80] font-semibold text-lg transition-colors"
            data-testid="website-link"
          >
            www.momsbiznetwork.co.za
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          </a>
        </div>
      </div>

      {/* Linktree Link Section */}
      <div className="max-w-4xl mx-auto px-6 pb-12">
        <div className="bg-gradient-to-r from-[#cb9944] to-[#b08435] rounded-2xl p-6 shadow-md text-center">
          <p className="text-white/90 mb-3">
            All our links in one place – find everything MBN!
          </p>
          <a 
            href="https://linktr.ee/MomsBizNetwork" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-white hover:text-white/80 font-semibold text-lg transition-colors"
            data-testid="linktree-link"
          >
            🔗 linktr.ee/MomsBizNetwork
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          </a>
        </div>
      </div>
    </div>
  );
}
